import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ToastContainer } from "react-toastify";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import "react-toastify/dist/ReactToastify.css";

import AdminLayout from "./layouts/AdminLayout";
import BrandRoutes from "./routes/BrandRoutes";
import { shouldRenderBrandPortal } from "./config/portal";
import Login from "./pages/auth/Login";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import ScreenList from "./pages/screens/ScreenList";
import ForgotPassword from "./pages/auth/ForgotPassword";
import ResetPassword from "./pages/auth/ResetPassword";
import VerifyOTP from "./pages/auth/VerifyOTP";
import Dashboard from "./pages/screens/Dashboard";
import UserList from "./pages/screens/UserList";
import Settings from "./pages/screens/Settings";
import Profile from "./pages/screens/Profile";
import NotFound from "./pages/errors/NotFound";
import Unauthorized from "./pages/errors/Unauthorized";
import TenantList from "./pages/screens/TenantList";
import Scheduler from "./pages/screens/Scheduler";
import Schedules from "./pages/screens/Schedules";
import RoleList from "./pages/screens/RoleList";
import ClientPlaylists from "./pages/screens/ClientPlaylists";
import Creatives from "./pages/screens/Creatives";
import Playlists from "./pages/screens/Playlists";
import ControlPanel from "./pages/screens/ControlPanel";
import PermissionsList from "./pages/screens/PermissionsList";
import ClientList from "./pages/screens/ClientList";
import BrandList from "./pages/screens/BrandList";
import AdminPage from "./pages/screens/AdminPage";
import NestedGroupIndex from "./components/navigation/NestedGroupIndex";
import AccountManagement from "./components/subComponents/AccountManagement";
import ScreenManagement from "./components/subComponents/ScreenManagement";
import Templates from "./components/subComponents/Templates";
import TenantConfiguration from "./components/subComponents/TenantConfiguration";
import TermsAndConditions from "./components/subComponents/TermsAndConditions";
import TenantAssets from "./components/subComponents/TenantAssets";
import PiSoftwareUpdates from "./components/subComponents/PiSoftwareUpdates";
import Reports from "./pages/screens/Reports";
import OnOffReport from "./components/subComponents/OnOffReport";
import CreativeReport from "./components/subComponents/CreativeReport";
import ActiveScreensReport from "./components/subComponents/ActiveScreensReport";

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <div className="min-h-screen bg-white">
          {shouldRenderBrandPortal() ? (
            <BrandRoutes />
          ) : (
          <Routes>
            {/* Public Auth Routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-otp" element={<VerifyOTP />} />

            {/* Protected Routes */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              {/* Redirect root path to dashboard */}
              <Route index element={<Navigate to="/dashboard" replace />} />
              {/* Dashboard is accessible to all authenticated users */}
              <Route path="dashboard" element={<Dashboard />} />

              {/* Admin nested routes - consolidated all admin routes here */}
              <Route
                path="admin"
                element={
                  <ProtectedRoute>
                    <AdminPage />
                  </ProtectedRoute>
                }
              >
                <Route
                  path="users"
                  element={
                    <ProtectedRoute route="users">
                      <UserList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="roles"
                  element={
                    <ProtectedRoute route="roles">
                      <RoleList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="clients"
                  element={
                    <ProtectedRoute route="clients">
                      <ClientList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="brands"
                  element={
                    <ProtectedRoute route="brands">
                      <BrandList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="tenants"
                  element={
                    <ProtectedRoute route="tenants">
                      <TenantList />
                    </ProtectedRoute>
                  }
                />
                <Route index element={<NestedGroupIndex groupId="admin" />} />
              </Route>

              {/* Direct routes for superadmin (tenants, roles, permissions) - kept for backward compatibility */}
              <Route
                path="tenants"
                element={
                  <ProtectedRoute route="tenants">
                    <TenantList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="roles"
                element={
                  <ProtectedRoute route="roles">
                    <RoleList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="permissions"
                element={
                  <ProtectedRoute route="permissions">
                    <PermissionsList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="pisoftwareupdates"
                element={
                  <ProtectedRoute route="pi_ota">
                    <PiSoftwareUpdates />
                  </ProtectedRoute>
                }
              />
              <Route
                path="screens"
                element={
                  <ProtectedRoute route="screens">
                    <ScreenList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="creatives"
                element={
                  <ProtectedRoute route="creatives">
                    <Creatives />
                  </ProtectedRoute>
                }
              />
              <Route
                path="settings"
                element={
                  <ProtectedRoute route="settings">
                    <Settings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="playlist"
                element={
                  <ProtectedRoute route="playlist">
                    <DndProvider backend={HTML5Backend}>
                      <Playlists />
                    </DndProvider>
                  </ProtectedRoute>
                }
              />
              <Route
                path="clientplaylist"
                element={
                  <ProtectedRoute route="clientplaylist">
                    <DndProvider backend={HTML5Backend}>
                      <ClientPlaylists />
                    </DndProvider>
                  </ProtectedRoute>
                }
              />
              {/* Control Panel nested routes */}
              <Route
                path="controlpanel"
                element={
                  <ProtectedRoute
                    route={[
                      "screens",
                      "templates",
                      "accountmanagement",
                      "tenantconfiguration",
                    ]}
                  >
                    <ControlPanel />
                  </ProtectedRoute>
                }
              >
                <Route
                  path="screenmanagement"
                  element={
                    <ProtectedRoute route="screens">
                      <ScreenManagement />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="accountmanagement"
                  element={
                    <ProtectedRoute route="accountmanagement">
                      <AccountManagement />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="templates"
                  element={
                    <ProtectedRoute route="templates">
                      <Templates />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="tenantconfiguration"
                  element={
                    <ProtectedRoute route="tenantconfiguration">
                      <TenantConfiguration />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="tenantassets"
                  element={
                    <ProtectedRoute route="tenantconfiguration">
                      <TenantAssets />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="termsandconditions"
                  element={
                    <ProtectedRoute route="tenantconfiguration">
                      <TermsAndConditions />
                    </ProtectedRoute>
                  }
                />
                <Route
                  index
                  element={<NestedGroupIndex groupId="controlpanel" />}
                />
              </Route>
              <Route
                path="scheduler"
                element={
                  <ProtectedRoute route="scheduler">
                    <Scheduler />
                  </ProtectedRoute>
                }
              />
              <Route
                path="schedules"
                element={
                  <ProtectedRoute route="scheduler">
                    <Schedules />
                  </ProtectedRoute>
                }
              />
              {/* Reports nested routes */}
              <Route
                path="reports"
                element={
                  <ProtectedRoute>
                    <Reports />
                  </ProtectedRoute>
                }
              >
                <Route
                  path="onoff"
                  element={
                    <ProtectedRoute route="reports">
                      <OnOffReport />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="creative"
                  element={
                    <ProtectedRoute route="reports">
                      <CreativeReport />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="activescreens"
                  element={
                    <ProtectedRoute route="reports">
                      <ActiveScreensReport />
                    </ProtectedRoute>
                  }
                />
                <Route index element={<NestedGroupIndex groupId="reports" />} />
              </Route>
              <Route
                path="profile"
                element={
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                }
              />
            </Route>

            {/* Error Routes */}
            <Route path="/unauthorized" element={<Unauthorized />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
          )}

          {/* Global toast notifications */}
          <ToastContainer position="top-right" />
        </div>
      </Router>
    </QueryClientProvider>
  );
}

export default App;
